import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api, { apiError, API_BASE } from "@/lib/api";
import { PageContainer, PageHeader } from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Sparkles, Loader2, Download, FileText, Save, Upload } from "lucide-react";
import { toast } from "sonner";
import LexduoPicker from "@/components/LexduoPicker";
import VariablesHelp from "@/components/VariablesHelp";

export default function Generator() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [skills, setSkills] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [suggestions, setSuggestions] = useState({});
  const [lexduoEnabled, setLexduoEnabled] = useState(false);
  const [uploading, setUploading] = useState(false);
  // Datos resueltos del expediente LexDuo seleccionado (para panel de variables y participantes)
  const [expedienteData, setExpedienteData] = useState(null);
  const [participantes, setParticipantes] = useState([]);

  const [form, setForm] = useState({
    template_id: "",
    skill_id: "",
    title: "",
    expediente_id: params.get("expediente") || "",
    expediente_id_lexduo: null,
    contacto_id_lexduo: null,
    variables: {},
    writing_document_ids: [],
    reference_document_ids: [],
    instructions: "",
  });

  useEffect(() => {
    (async () => {
      try {
        const [a, c, s, sk] = await Promise.all([
          api.get("/templates"), api.get("/documents"),
          api.get("/lexduo/status"),
          api.get("/skills"),
        ]);
        setTemplates(a.data);
        setDocuments(c.data);
        setLexduoEnabled(!!s.data?.enabled);
        setSkills(sk.data || []);
      } catch (e) { toast.error(apiError(e)); }
    })();
  }, []);

  // Cuando cambia el expediente LexDuo, cargar sus datos + participantes
  useEffect(() => {
    if (!form.expediente_id_lexduo) {
      setExpedienteData(null);
      setParticipantes([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const [expR, partR] = await Promise.all([
          api.get(`/lexduo/expedientes/${form.expediente_id_lexduo}`),
          api.get(`/lexduo/expedientes/${form.expediente_id_lexduo}/participantes`).catch(() => ({ data: [] })),
        ]);
        if (cancelled) return;
        setExpedienteData(expR.data || null);
        setParticipantes(Array.isArray(partR.data) ? partR.data : (partR.data?.data || []));
      } catch {
        if (!cancelled) { setExpedienteData(null); setParticipantes([]); }
      }
    })();
    return () => { cancelled = true; };
  }, [form.expediente_id_lexduo]);

  // Preselect template from ?template= query (e.g. coming from marketplace)
  useEffect(() => {
    const tid = params.get("template");
    if (tid && templates.find((t) => t.id === tid)) {
      setForm((f) => ({ ...f, template_id: tid, variables: {} }));
    }
    // eslint-disable-next-line
  }, [templates]);

  // Load variable suggestions when template changes
  useEffect(() => {
    if (!form.template_id) { setSuggestions({}); return; }
    api.get(`/templates/${form.template_id}/suggestions`)
      .then(({ data }) => {
        const map = {};
        for (const s of data || []) map[s.variable] = s.values || [];
        setSuggestions(map);
      })
      .catch(() => setSuggestions({}));
  }, [form.template_id]);

  const selectedTemplate = templates.find((t) => t.id === form.template_id);
  const writings = documents.filter((d) => ["escrito_judicial", "contrato", "otro"].includes(d.category));
  const references = documents.filter((d) => ["sentencia", "auto", "informe", "otro"].includes(d.category));

  const toggleArr = (field, id) => {
    setForm((f) => {
      const has = f[field].includes(id);
      return { ...f, [field]: has ? f[field].filter((x) => x !== id) : [...f[field], id] };
    });
  };

  const generate = async () => {
    if (!form.title) return toast.error("Indica un título");
    setLoading(true);
    try {
      const payload = {
        ...form,
        template_id: form.template_id || null,
        expediente_id: form.expediente_id || null,
        expediente_id_lexduo: form.expediente_id_lexduo || null,
        contacto_id_lexduo: form.contacto_id_lexduo || null,
      };
      const { data } = await api.post("/generator/generate", payload);
      setResult(data);
      toast.success("Documento generado");
    } catch (e) {
      toast.error(apiError(e));
    } finally { setLoading(false); }
  };

  const uploadToLexduo = async () => {
    if (!result) return;
    const expId = form.expediente_id_lexduo || result.expediente_id_lexduo;
    if (!expId) return toast.error("Selecciona un expediente de LexDuo antes de subir");
    setUploading(true);
    try {
      const { data } = await api.post(`/generator/${result.id}/upload-to-lexduo`, {
        expediente_id: expId, format: "docx",
      });
      toast.success(`Subido a LexDuo como "${data.filename}"`);
      setResult({ ...result, uploaded_to_lexduo: data });
    } catch (e) { toast.error(apiError(e)); }
    finally { setUploading(false); }
  };

  const save = async () => {
    if (!result) return;
    try {
      await api.patch(`/generator/${result.id}`, { title: result.title, body: result.body });
      toast.success("Cambios guardados");
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <PageContainer>
      <PageHeader
        title="Generador IA"
        subtitle="Combina plantillas, escritos judiciales y documentación de referencia para redactar un documento jurídico con IA."
      />

      <div className="grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-6">
        {/* Config panel */}
        <div className="space-y-4 bg-white border border-[#E5E7EB] p-5 self-start sticky top-4 max-h-[calc(100vh-3rem)] overflow-y-auto">
          <div>
            <Label className="text-xs uppercase tracking-wider">Título del documento *</Label>
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="gen-title-input" placeholder="p.ej. Demanda de reclamación..." className="rounded-sm mt-1" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider">Plantilla base</Label>
            <Select value={form.template_id || "_none"} onValueChange={(v) => setForm({ ...form, template_id: v === "_none" ? "" : v, variables: {} })}>
              <SelectTrigger className="rounded-sm mt-1" data-testid="gen-template-select"><SelectValue placeholder="Sin plantilla" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_none">— Sin plantilla —</SelectItem>
                {templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider flex items-center gap-2">
              Skill / Modo de redacción IA
              <span className="text-neutral-400 text-[10px] font-normal normal-case tracking-normal">opcional</span>
            </Label>
            <Select value={form.skill_id || "_none"} onValueChange={(v) => setForm({ ...form, skill_id: v === "_none" ? "" : v })}>
              <SelectTrigger className="rounded-sm mt-1" data-testid="gen-skill-select"><SelectValue placeholder="Sin skill" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_none">— Sin skill —</SelectItem>
                {skills.map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.category ? `${s.category} · ` : ""}{s.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {form.skill_id && (
              <p className="text-[11px] text-neutral-500 mt-1 italic">
                {skills.find((s) => s.id === form.skill_id)?.description || "Instrucciones IA predefinidas aplicadas."}
              </p>
            )}
          </div>

          {/* LexDuo integration pickers */}
          <LexduoPicker
            kind="expediente"
            value={form.expediente_id_lexduo}
            onChange={(id) => setForm({ ...form, expediente_id_lexduo: id })}
          />
          <LexduoPicker
            kind="contacto"
            value={form.contacto_id_lexduo}
            onChange={(id) => setForm({ ...form, contacto_id_lexduo: id })}
          />

          <VariablesHelp />

          {/* Expediente context panel — variables disponibles + participantes */}
          {(expedienteData || participantes.length > 0) && (
            <div className="border border-[#7B1826]/20 bg-[#FDF5F7] rounded-sm p-3 text-xs space-y-2">
              <div className="font-medium text-[#7B1826] flex items-center justify-between">
                <span>Datos disponibles del expediente</span>
                <Badge variant="outline" className="rounded-sm text-[9px] border-[#7B1826]/30 text-[#7B1826]">
                  {participantes.length} participante{participantes.length === 1 ? "" : "s"}
                </Badge>
              </div>
              {expedienteData && (
                <div className="text-neutral-700 leading-relaxed">
                  <span className="font-mono font-semibold">
                    {expedienteData.numero_expediente || `#${expedienteData.id}`}
                  </span>
                  {expedienteData.titulo && <span> · {expedienteData.titulo.slice(0, 80)}{expedienteData.titulo.length > 80 ? "…" : ""}</span>}
                  <div className="mt-1 flex flex-wrap gap-1">
                    {[
                      ["rama", expedienteData.rama],
                      ["fase", expedienteData.fase],
                      ["juzgado", expedienteData.juzgado_nombre || expedienteData.juzgado],
                      ["contrario", expedienteData.contrario],
                    ].filter(([_, v]) => v).map(([k, v]) => (
                      <Badge key={k} variant="outline" className="rounded-sm text-[9px] border-[#E5E7EB]">
                        {k}: {String(v).slice(0, 30)}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
              {participantes.length > 0 && (
                <details className="text-[11px] text-neutral-600">
                  <summary className="cursor-pointer hover:text-[#7B1826]">Ver participantes e intervinientes ({participantes.length})</summary>
                  <ul className="mt-1.5 space-y-0.5 pl-3">
                    {participantes.slice(0, 10).map((p, i) => {
                      const nombre = p.nombre || p.nombre_completo || p.razon_social || "(sin nombre)";
                      const rol = p.rol || p.tipo || p.tipo_participante || "";
                      return (
                        <li key={i} className="truncate">
                          <span className="font-medium">{nombre}</span>
                          {rol && <span className="text-neutral-500"> · {rol}</span>}
                        </li>
                      );
                    })}
                    {participantes.length > 10 && <li className="text-neutral-400 italic">…+{participantes.length - 10} más</li>}
                  </ul>
                </details>
              )}
              <div className="text-[10px] text-neutral-500 pt-1 border-t border-[#7B1826]/10">
                La IA recibirá automáticamente todos estos datos (expediente, cliente y participantes) como contexto al redactar.
              </div>
            </div>
          )}

          {selectedTemplate?.variables?.length > 0 && (
            <div>
              <div className="text-xs uppercase tracking-wider mb-2 text-neutral-600 flex items-center gap-2">
                <span>Variables de plantilla</span>
                {lexduoEnabled && (form.expediente_id_lexduo || form.contacto_id_lexduo) && (
                  <Badge className="rounded-sm text-[9px] bg-emerald-50 text-emerald-700 border-emerald-200 border hover:bg-emerald-50">
                    Autorrelleno LexDuo activo
                  </Badge>
                )}
              </div>
              <div className="space-y-2">
                {selectedTemplate.variables.map((v) => {
                  const sugs = suggestions[v] || [];
                  const listId = `sugs-${v}`;
                  const isLxVar = v.startsWith("expediente.") || v.startsWith("cliente.");
                  const willAutofill = isLxVar && (
                    (v.startsWith("expediente.") && form.expediente_id_lexduo) ||
                    (v.startsWith("cliente.") && form.contacto_id_lexduo)
                  );
                  return (
                    <div key={v}>
                      <Label className="text-[11px] font-mono text-[#7B1826] flex items-center gap-1">
                        {"{{"+v+"}}"}
                        {willAutofill && <span className="text-[9px] text-emerald-700 font-sans normal-case tracking-normal">(autorrelleno)</span>}
                      </Label>
                      <Input
                        list={sugs.length ? listId : undefined}
                        value={form.variables[v] || ""}
                        onChange={(e) => setForm({ ...form, variables: { ...form.variables, [v]: e.target.value } })}
                        className="rounded-sm mt-0.5 h-9 text-sm"
                        placeholder={willAutofill ? "(se rellenará desde LexDuo)" : ""}
                        data-testid={`gen-var-${v}`}
                      />
                      {sugs.length > 0 && (
                        <>
                          <datalist id={listId}>
                            {sugs.map((s) => <option key={s.value} value={s.value} />)}
                          </datalist>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {sugs.slice(0, 3).map((s) => (
                              <button
                                key={s.value}
                                type="button"
                                onClick={() => setForm({ ...form, variables: { ...form.variables, [v]: s.value } })}
                                className="text-[10px] px-1.5 py-0.5 bg-[#F3F4F6] hover:bg-[#7B1826]/10 hover:text-[#7B1826] text-neutral-600 rounded-sm font-mono transition-colors truncate max-w-[200px]"
                                title={`${s.value} — usado ${s.count} ${s.count === 1 ? "vez" : "veces"}`}
                                data-testid={`gen-suggestion-${v}-${s.count}`}
                              >
                                {s.value.length > 28 ? s.value.slice(0, 28) + "…" : s.value}
                                <span className="ml-1 text-neutral-400">×{s.count}</span>
                              </button>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div>
            <div className="text-xs uppercase tracking-wider mb-2 text-neutral-600">
              Escritos judiciales a analizar ({form.writing_document_ids.length})
            </div>
            <div className="max-h-40 overflow-y-auto border border-[#E5E7EB] rounded-sm">
              {writings.length === 0 ? <div className="p-3 text-xs text-neutral-500">No hay escritos cargados</div> :
                writings.map((d) => (
                  <label key={d.id} className="flex items-start gap-2 p-2 text-xs hover:bg-[#FBFBF9] cursor-pointer border-b border-[#E5E7EB] last:border-0">
                    <Checkbox
                      checked={form.writing_document_ids.includes(d.id)}
                      onCheckedChange={() => toggleArr("writing_document_ids", d.id)}
                      data-testid={`gen-writing-${d.id}`}
                      className="mt-0.5"
                    />
                    <span className="truncate">{d.original_name}</span>
                  </label>
                ))}
            </div>
          </div>

          <div>
            <div className="text-xs uppercase tracking-wider mb-2 text-neutral-600">
              Documentos de referencia ({form.reference_document_ids.length})
            </div>
            <div className="max-h-40 overflow-y-auto border border-[#E5E7EB] rounded-sm">
              {references.length === 0 ? <div className="p-3 text-xs text-neutral-500">No hay referencias cargadas</div> :
                references.map((d) => (
                  <label key={d.id} className="flex items-start gap-2 p-2 text-xs hover:bg-[#FBFBF9] cursor-pointer border-b border-[#E5E7EB] last:border-0">
                    <Checkbox
                      checked={form.reference_document_ids.includes(d.id)}
                      onCheckedChange={() => toggleArr("reference_document_ids", d.id)}
                      data-testid={`gen-reference-${d.id}`}
                      className="mt-0.5"
                    />
                    <span className="truncate">{d.original_name} <span className="text-neutral-400">· {d.category}</span></span>
                  </label>
                ))}
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider">Instrucciones adicionales</Label>
            <Textarea
              value={form.instructions}
              onChange={(e) => setForm({ ...form, instructions: e.target.value })}
              rows={4}
              className="rounded-sm mt-1 text-sm"
              placeholder="Tono, énfasis en ciertos argumentos, formato específico..."
              data-testid="gen-instructions-textarea"
            />
          </div>

          <Button onClick={generate} disabled={loading || !form.title} data-testid="gen-submit-button" className="w-full rounded-sm bg-[#7B1826] hover:bg-[#5D121D] h-11">
            {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Redactando…</> : <><Sparkles className="w-4 h-4 mr-2" /> Generar documento</>}
          </Button>
        </div>

        {/* Preview */}
        <div>
          {result ? (
            <>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="text-[11px] uppercase tracking-widest text-neutral-500">Documento generado</div>
                  <Input
                    value={result.title}
                    onChange={(e) => setResult({ ...result, title: e.target.value })}
                    className="font-display text-2xl border-0 px-0 h-auto focus-visible:ring-0 shadow-none rounded-none"
                  />
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={save} className="rounded-sm" data-testid="gen-save-button"><Save className="w-4 h-4 mr-2" />Guardar</Button>
                  <a href={`${API_BASE}/export/docx/${result.id}`} className="inline-flex"><Button variant="outline" className="rounded-sm"><Download className="w-4 h-4 mr-2" />DOCX</Button></a>
                  <a href={`${API_BASE}/export/pdf/${result.id}`} className="inline-flex"><Button variant="outline" className="rounded-sm"><FileText className="w-4 h-4 mr-2" />PDF</Button></a>
                  {lexduoEnabled && (form.expediente_id_lexduo || result.expediente_id_lexduo) && (
                    <Button
                      onClick={uploadToLexduo}
                      disabled={uploading}
                      className="rounded-sm bg-[#7B1826] hover:bg-[#5D121D]"
                      data-testid="gen-upload-lexduo-button"
                    >
                      {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                      {result.uploaded_to_lexduo ? "Re-subir a LexDuo" : "Subir a LexDuo"}
                    </Button>
                  )}
                </div>
              </div>
              {result.uploaded_to_lexduo && (
                <div className="mb-3 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-sm px-3 py-2 flex items-center gap-2">
                  <Upload className="w-3 h-3" />
                  Adjunto en LexDuo expediente #{result.uploaded_to_lexduo.expediente_id} ·
                  <span className="font-mono">{result.uploaded_to_lexduo.filename}</span>
                </div>
              )}
              <Textarea
                value={result.body}
                onChange={(e) => setResult({ ...result, body: e.target.value })}
                className="paper font-normal text-[14px] leading-7 focus-visible:ring-0 border-0 rounded-none resize-none"
                rows={30}
                data-testid="gen-output-textarea"
                style={{ boxShadow: "0 1px 2px rgba(0,0,0,.04), 0 8px 24px rgba(0,0,0,.06)" }}
              />
            </>
          ) : (
            <div className="paper flex flex-col items-center justify-center text-center text-neutral-500">
              <Sparkles className="w-12 h-12 text-[#7B1826]/30" />
              <h3 className="font-display text-2xl mt-4 text-[#0F172A]">Tu documento aparecerá aquí</h3>
              <p className="text-sm mt-2 max-w-md">
                Selecciona plantilla, escritos y referencias a la izquierda y pulsa <b>Generar documento</b>.
                La IA redactará un borrador combinando toda la información.
              </p>
            </div>
          )}
        </div>
      </div>
    </PageContainer>
  );
}
